// TMOS Setup Reminder Component
// Date: December 19, 2025
// Purpose: Reminder banner/notification for incomplete setup

import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { setupWizardService, SetupType, SetupProgress } from '@/services/setupWizardService';
import { SetupWizardModal } from './SetupWizardModal';
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface SetupReminderProps {
  setupType: SetupType;
  onDismiss?: () => void;
}

/**
 * SetupReminder Component
 * 
 * Displays a reminder banner when user setup is incomplete.
 * Shows for the first month after registration.
 */
export const SetupReminder: React.FC<SetupReminderProps> = ({
  setupType,
  onDismiss
}) => {
  const { t } = useLanguage();
  const [showReminder, setShowReminder] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [progress, setProgress] = useState<SetupProgress | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkSetupStatus();
  }, [setupType]);

  const checkSetupStatus = async () => {
    try {
      setLoading(true);
      const currentProgress = await setupWizardService.getUserSetupProgress(setupType);
      
      if (currentProgress && !currentProgress.is_complete) {
        // Check if setup was started within the last month
        const startedAt = new Date(currentProgress.started_at);
        const oneMonthAgo = new Date();
        oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

        if (startedAt >= oneMonthAgo) {
          setProgress(currentProgress);
          setShowReminder(true);
        }
      }
    } catch (error) {
      console.error('Error checking setup status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartSetup = () => {
    setShowWizard(true);
  };

  const handleDismiss = () => {
    setShowReminder(false);
    onDismiss?.();
  };

  const handleWizardComplete = () => {
    setShowWizard(false);
    setShowReminder(false);
    checkSetupStatus();
  };

  if (loading || !showReminder || !progress) {
    return null;
  }

  const completionPercentage = progress.completion_percentage || 0;

  return (
    <>
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
        <div className="flex items-start">
          <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mt-0.5 mr-3" />
          <div className="flex-1">
            <h3 className="text-sm font-medium text-yellow-800">
              {t('setupWizard.reminder.title') || 'Complete Your Setup'}
            </h3>
            <div className="mt-2 text-sm text-yellow-700">
              <p>
                {t('setupWizard.reminder.message') || 
                  `Your setup is ${completionPercentage}% complete. Complete your setup to unlock all features.`}
              </p>
            </div>
            <div className="mt-4">
              <div className="flex items-center gap-3">
                <button
                  onClick={handleStartSetup}
                  className="bg-yellow-400 text-yellow-900 px-4 py-2 rounded-md text-sm font-medium hover:bg-yellow-500"
                >
                  {t('setupWizard.reminder.startSetup') || 'Complete Setup'}
                </button>
                <button
                  onClick={handleDismiss}
                  className="text-yellow-700 hover:text-yellow-900 text-sm"
                >
                  {t('common.dismiss') || 'Dismiss'}
                </button>
              </div>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="ml-4 text-yellow-400 hover:text-yellow-500"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {showWizard && (
        <SetupWizardModal
          isOpen={showWizard}
          onClose={() => setShowWizard(false)}
          setupType={setupType}
          onComplete={handleWizardComplete}
        />
      )}
    </>
  );
};

